import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ApiError, api } from '@/api/client'
import { putWithProgress } from '@/api/upload'
import { useProfile } from '@/auth/ProfileContext'
import { Icon } from '@/components/Icon'
import { IntakeAudioUpload } from './IntakeAudioUpload'
import './template-intake.css'
import { ResearchReview } from './ResearchReview'
import { TemplateSource } from './TemplateSource'
import { INTAKE_API, intakeDownload, intakeStatus, isSubmitted, recovery, type IntakeDetail, type IntakeFile, type IntakeText } from '@/lib/templateIntakes'
/** Statuses in which the page leads with proposals, so the source data starts folded. */
const REVIEWING: IntakeDetail['template']['status'][] = ['open', 'claimed', 'approved']
const IntakeDictation = lazy(() => import('./IntakeDictation').then(module => ({ default: module.IntakeDictation })))

const textOf = (value: IntakeDetail): IntakeText => ({ title: value.title, explanation: value.explanation, partial: value.partial })
function downloadText(text: IntakeText, filename: string) {
  const url = URL.createObjectURL(new Blob([[text.explanation, text.partial].filter(Boolean).join('\n')], { type: 'text/plain;charset=utf-8' }))
  const link = document.createElement('a'); link.href = url; link.download = filename; link.click()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export default function TemplateIntake({ developer = false }: { developer?: boolean }) {
  const { templateId } = useParams()
  const [params] = useSearchParams()
  return <IntakeEditor key={`${templateId}:${params.get('submission') ?? ''}`} developer={developer} />
}

function IntakeEditor({ developer }: { developer: boolean }) {
  const { templateId } = useParams()
  const [params] = useSearchParams()
  const historical = params.get('submission') ?? ''
  const { profile } = useProfile()
  const navigate = useNavigate()
  const [data, setData] = useState<IntakeDetail | null>(null)
  const [draft, setDraft] = useState<IntakeText>({ title: '', explanation: '', partial: '' })
  const [error, setError] = useState('')
  const [status, setStatus] = useState('')
  const [busy, setBusy] = useState(false)
  const [speaking, setSpeaking] = useState(false)
  const [transcribing, setTranscribing] = useState(false)
  const transcribingRef = useRef(false)
  transcribingRef.current = transcribing
  const [reason, setReason] = useState('')
  const [progress, setProgress] = useState('')
  const [conflict, setConflict] = useState(false)
  const [recoveryError, setRecoveryError] = useState('')
  const explanationRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const current = useRef<IntakeDetail | null>(null)
  const draftRef = useRef(draft)
  const saved = useRef('')
  const blocked = useRef(false)
  const savePromise = useRef<Promise<void> | null>(null)
  const busyRef = useRef(false)
  const cacheKey = `${profile?.user_id}:${templateId}`
  const base = `${INTAKE_API}/${templateId}`
  const initial = useRef<{ key: string; promise: Promise<IntakeDetail> }>()

  function receive(value: IntakeDetail) { current.current = value; setData(value) }
  async function refreshSource() {
    // Finish an existing autosave before reconciling its baseline with the refreshed text.
    if (savePromise.current) await savePromise.current.catch(() => {})
    const value = await api.get<IntakeDetail>(base)
    const text = textOf(value)
    const remote = JSON.stringify(text)
    const local = JSON.stringify(draftRef.current)
    const dirty = local !== saved.current
    receive(value)
    if (dirty && remote !== saved.current && remote !== local) {
      blocked.current = true; setConflict(true); setStatus('Saving paused')
      setError('The template explanation changed on the server. Download your local text before loading the latest version.')
      return
    }
    if (!dirty || remote === local) {
      draftRef.current = text; setDraft(text); saved.current = remote
      await recovery.remove(cacheKey).catch(() => {})
    } else {
      // File or assignment changes can advance the revision without changing the saved text.
      await recovery.put(cacheKey, { ...draftRef.current, revision: value.template.revision }).catch(() => {})
    }
    blocked.current = false; setConflict(false); setError('')
    setStatus(dirty && remote !== local ? 'Unsaved changes' : 'Saved')
  }
  function update(value: IntakeText) {
    draftRef.current = value; setDraft(value)
    const row = current.current
    if (row?.can_edit || row?.can_edit_source) {
      setStatus('Unsaved changes')
      void recovery.put(cacheKey, { ...value, revision: row.template.revision }).catch(() => {
        if (JSON.stringify(draftRef.current) !== saved.current) setStatus('Local recovery unavailable; keep this page open until saved.')
      })
    }
  }
  useEffect(() => {
    if (!profile || !templateId) return
    let alive = true
    const key = `${templateId}:${historical}`
    if (initial.current?.key !== key) initial.current = { key, promise: templateId === 'new'
      ? api.post<IntakeDetail>(INTAKE_API) : api.get<IntakeDetail>(base, historical ? { submission: historical } : undefined) }
    void initial.current.promise.then(async value => {
      if (!alive) return
      if (templateId === 'new') { navigate(`/sme/templates/${value.template.template_id}`, { replace: true }); return }
      let text = textOf(value)
      const local = value.can_edit || value.can_edit_source ? await recovery.get(cacheKey).catch(() => undefined) : undefined
      if (!alive) return
      saved.current = JSON.stringify(text)
      receive(value)
      if (local && JSON.stringify({ title: local.title, explanation: local.explanation, partial: local.partial }) !== saved.current) {
        text = { title: local.title, explanation: local.explanation, partial: local.partial }
        if (local.revision !== value.template.revision) {
          blocked.current = true; setConflict(true); setError('Your recovered draft differs from a newer server revision. Copy your text before reloading.')
        } else setStatus('Recovered unsaved text')
      }
      draftRef.current = text; setDraft(text)
    }).catch(e => { if (alive) setError(e.message) })
    return () => { alive = false }
  }, [templateId, historical, profile?.user_id])

  async function save(): Promise<void> {
    if (savePromise.current) { await savePromise.current; return save() }
    const row = current.current
    if (!(row?.can_edit || row?.can_edit_source) || JSON.stringify(draftRef.current) === saved.current) return
    if (blocked.current) throw new Error('Resolve the revision conflict before saving.')
    const snapshot = { ...draftRef.current }
    const operation = (async () => {
      setStatus('Saving…')
      let value: IntakeDetail
      try {
        value = await api.put<IntakeDetail>(base, { ...snapshot, revision: row.template.revision })
      } catch (e) {
        if (e instanceof ApiError && e.status === 409) { blocked.current = true; setConflict(true) }
        setStatus('Not saved — your local text has been kept'); setError((e as Error).message)
        throw e
      }
      saved.current = JSON.stringify(snapshot); receive(value); setError('')
      let recoveryAvailable = true
      try {
        if (JSON.stringify(draftRef.current) === saved.current) await recovery.remove(cacheKey)
        else await recovery.put(cacheKey, { ...draftRef.current, revision: value.template.revision })
      } catch { recoveryAvailable = false }
      setStatus(JSON.stringify(draftRef.current) === saved.current ? 'Saved' : recoveryAvailable
        ? 'Unsaved changes' : 'Local recovery unavailable; keep this page open until saved.')
    })()
    savePromise.current = operation
    try { await operation } finally { if (savePromise.current === operation) savePromise.current = null }
  }
  const saveRef = useRef(save)
  saveRef.current = save
  useEffect(() => {
    const timer = window.setInterval(() => { if (!busyRef.current && !blocked.current) void saveRef.current().catch(() => {}) }, 2000)
    const warn = (event: BeforeUnloadEvent) => {
      if (JSON.stringify(draftRef.current) !== saved.current || busyRef.current || transcribingRef.current) { event.preventDefault(); event.returnValue = '' }
    }
    window.addEventListener('beforeunload', warn)
    return () => { window.clearInterval(timer); window.removeEventListener('beforeunload', warn) }
  }, [])

  async function mutate(action: () => Promise<void>) {
    if (busyRef.current) return
    busyRef.current = true; setBusy(true); setError('')
    try { await save(); await action() } catch (e) { setError((e as Error).message) }
    finally { busyRef.current = false; setBusy(false); setProgress('') }
  }
  async function upload(files: FileList) {
    const selected = Array.from(files)
    await mutate(async () => {
      if (selected.length + current.current!.files.length > data!.max_files) throw new Error('Up to 10 files per template. Archive larger collections first.')
      for (const file of selected) {
        if (!file.size || file.size > data!.max_file_bytes) throw new Error(`${file.name}: choose a nonempty file up to 5 GiB.`)
        const contentType = file.type || 'application/octet-stream'
        const registered = await api.post<{ template: IntakeDetail['template']; file: IntakeFile; upload_url: string }>(`${base}/files`, {
          revision: current.current!.template.revision, filename: file.name, size: file.size, content_type: contentType,
        })
        receive({ ...current.current!, template: registered.template, files: [...current.current!.files, registered.file] })
        await putWithProgress(registered.upload_url, file, contentType, pct => setProgress(`${file.name}: ${pct}%`))
        receive(await api.post<IntakeDetail>(`${base}/files/${registered.file.file_id}/complete`, { revision: current.current!.template.revision }))
      }
    })
  }
  useEffect(() => {
    const canAttach = () => !!data?.can_edit && !developer && !busyRef.current && !speaking && !blocked.current
    const dragOver = (event: DragEvent) => {
      if (!event.dataTransfer?.types.includes('Files')) return
      event.preventDefault()
      event.dataTransfer.dropEffect = canAttach() ? 'copy' : 'none'
    }
    const drop = (event: DragEvent) => {
      if (!event.dataTransfer?.types.includes('Files')) return
      event.preventDefault()
      if (canAttach() && event.dataTransfer.files.length) void upload(event.dataTransfer.files)
    }
    window.addEventListener('dragover', dragOver)
    window.addEventListener('drop', drop)
    return () => {
      window.removeEventListener('dragover', dragOver)
      window.removeEventListener('drop', drop)
    }
  }, [data, developer, speaking])
  function keepPartial() {
    const text = draftRef.current
    if (text.partial) update({ ...text, explanation: [text.explanation, text.partial].filter(Boolean).join('\n'), partial: '' })
  }
  function stopped() {
    keepPartial()
    void saveRef.current().catch(() => {})
  }
  useEffect(() => {
    const editor = explanationRef.current
    if (!editor) return
    const resize = () => {
      editor.style.height = 'auto'
      editor.style.height = `${editor.scrollHeight}px`
    }
    resize()
    let width = editor.clientWidth
    const observer = new ResizeObserver(() => {
      if (editor.clientWidth !== width) { width = editor.clientWidth; resize() }
    })
    observer.observe(editor)
    return () => observer.disconnect()
  }, [draft.explanation, data?.can_edit])

  if (!data) return <div className="narrow">{error ? <p className="note err" role="alert">{error}</p> : <p role="status">Loading template…</p>}</div>
  const editable = data.can_edit && !developer
  const byteCount = new TextEncoder().encode(draft.explanation + draft.partial).length
  return <div className="intake-page">
    <header className="intake-page-bar">
      <Link className="backlink" to={developer ? '/eng' : '/sme'}>← {developer ? 'Pipeline' : 'My data'}</Link>
      <span className="chip neutral">{intakeStatus(data.template.status)}</span>
    </header>
    {data.template.return_note && <p className="note intake-feedback">Developer feedback: {data.template.return_note}</p>}
    {data.submissions.length > 1 && <label className="field intake-history"><span>Submitted version</span><select value={historical} onChange={e => navigate(`?${e.target.value ? `submission=${e.target.value}` : ''}`)}>
      <option value="">{developer ? 'Latest submission' : 'Current template'}</option>
      {data.submissions.map(s => <option key={s.submission_id} value={s.submission_id}>{new Date(s.submitted_at).toLocaleString()}</option>)}
    </select></label>}
    {editable ? <article className="intake-document" aria-label="Template document">
      {conflict && <section className="intake-conflict" role="alert" aria-label="Draft conflict">
        <Icon name="alert" size={18} />
        <div className="intake-conflict-content">
          <strong>A newer version is available</strong>
          <p>Your notes are safe here. Download a copy before loading the latest version.</p>
          <div className="intake-conflict-actions">
            <button className="ghost" onClick={() => {
              downloadText(draftRef.current, 'recovered-explanation.txt')
            }}><Icon name="download" size={15} />Download recovered explanation</button>
            <button disabled={busy} onClick={() => { setRecoveryError(''); void api.get<IntakeDetail>(base).then(async value => {
              const text = textOf(value); draftRef.current = text; setDraft(text); receive(value); saved.current = JSON.stringify(text)
              blocked.current = false; setConflict(false); setError(''); setStatus('Saved'); await recovery.remove(cacheKey).catch(() => {})
            }).catch(e => setRecoveryError(e.message)) }}>Use latest server version</button>
          </div>
          {recoveryError && <p className="err-text">{recoveryError}</p>}
        </div>
      </section>}
      <div className="intake-document-kind"><Icon name="template" size={16} />Template</div>
      <input className="intake-title" type="text" aria-label="Title (optional)" placeholder="Untitled template" maxLength={200}
        value={draft.title === 'Untitled template' ? '' : draft.title} disabled={busy || conflict}
        onChange={e => update({ ...draftRef.current, title: e.target.value })} />
      <section className="intake-attachments" aria-label="Experiment data">
        <div className="intake-attachment-bar">
          <input ref={fileInputRef} className="visually-hidden" type="file" aria-label="Add data files" tabIndex={-1} multiple
              disabled={busy || speaking || conflict} onChange={e => {
                if (e.target.files?.length) void upload(e.target.files); e.target.value = ''
              }} />
          <button className="ghost" disabled={busy || speaking || conflict} onClick={() => fileInputRef.current?.click()}><Icon name="paperclip" size={16} />Attach data</button>
          <span className="intake-file-limit">Up to 10 files · 5 GiB each</span>
        </div>
        {progress && <p className="muted small" role="status">{progress}</p>}
        {!!data.files.length && <ul className="intake-files">{data.files.map(file => <li key={file.file_id}>
          <Icon name="file" size={18} />
          <span className="intake-file-name">{file.filename}<small>{(file.size / 1024 / 1024).toFixed(1)} MiB{file.status === 'pending' && ' · Upload unfinished'}</small></span>
          {file.status === 'uploaded' && <button className="ghost" disabled={busy} onClick={() => void intakeDownload(templateId!, file.file_id, historical || undefined).catch(e => setError(e.message))}><Icon name="download" size={15} />Download</button>}
          <button className="ghost" disabled={busy || speaking || conflict} onClick={() => void mutate(async () => {
            receive(await api.post<IntakeDetail>(`${base}/files/${file.file_id}/remove`, { revision: current.current!.template.revision }))
          })}>Remove</button>
        </li>)}</ul>}
        {data.files.some(f => f.status === 'pending') && <p className="muted small">Remove an unfinished upload and choose the file again to retry.</p>}
      </section>
      <section className="intake-writing" aria-label="Explanation">
        <div className="intake-writing-bar">
          <label htmlFor="intake-explanation">Your notes</label>
          {data.speech_enabled && <div className="intake-note-actions">
            <IntakeAudioUpload base={base} disabled={busy || speaking || conflict || byteCount >= data.max_text_bytes} onActive={setTranscribing}
              onTranscript={text => update({ ...draftRef.current, explanation: [draftRef.current.explanation, text].filter(Boolean).join('\n\n') })} />
            <Suspense fallback={<span className="muted small">Loading dictation…</span>}><IntakeDictation base={base} disabled={busy || conflict || transcribing || byteCount >= data.max_text_bytes} onActive={active => {
              if (active) keepPartial()
              setSpeaking(active)
            }}
            onPartial={partial => update({ ...draftRef.current, partial })}
            onCommit={text => update({ ...draftRef.current, explanation: [draftRef.current.explanation, text].filter(Boolean).join('\n'), partial: '' })}
            onStopped={stopped} /></Suspense>
          </div>}
        </div>
        <span id="intake-explanation-hint" className="visually-hidden">Describe your experiments, how to interpret the data, and ideas for other workflows. Write or speak as much as you need.</span>
        <textarea ref={explanationRef} id="intake-explanation" className="intake-explanation" aria-label="Explanation" aria-describedby="intake-explanation-hint"
          placeholder={'Describe the experiment and what you measured.\nExplain the files, columns and units.\nSuggest workflows we could build from this data.'}
          rows={8} value={draft.explanation} disabled={busy || speaking || conflict}
          onChange={e => update({ ...draftRef.current, explanation: e.target.value })} />
        {draft.partial && <p className="prewrap intake-partial" aria-label="Unfinished dictation">{draft.partial}</p>}
        {draft.partial && !speaking && <button className="ghost" onClick={stopped}>Keep unfinished dictation in explanation</button>}
        {!data.speech_enabled && <p className="muted small">Dictation is unavailable here. Your notes are saved as you type.</p>}
        {byteCount > data.max_text_bytes && <p className="note err">The explanation exceeds 1 MiB. Shorten it before saving; your local text has been kept.</p>}
        {data.submissions.length > 0 && <button className="ghost" onClick={() => downloadText(draftRef.current, 'explanation.txt')}>
          <Icon name="download" size={16} />Download explanation</button>}
      </section>
      <footer className="intake-document-footer">
        <span className="intake-save-status" role="status">{conflict ? 'Saving paused' : status || 'All changes saved'}{byteCount >= data.max_text_bytes * .9 && ` · ${Math.ceil(byteCount / 1024)} / 1024 KiB`}</span>
        <div className="row">
          <button className="ghost" disabled={busy || conflict} onClick={() => void save().catch(() => {})}>Save draft</button>
          <button className="primary" disabled={busy || speaking || transcribing || conflict || !draft.explanation.trim() || !!draft.partial || byteCount > data.max_text_bytes
            || !data.files.length || data.files.some(f => f.status !== 'uploaded')} onClick={() => void mutate(async () => {
              receive(await api.post<IntakeDetail>(`${base}/submit`, { revision: current.current!.template.revision })); await recovery.remove(cacheKey).catch(() => {})
            })}>Submit template<Icon name="arrow" size={16} /></button>
        </div>
      </footer>
    </article> : <>
      <h1 className="intake-heading">{data.title}</h1>
      <TemplateSource files={data.files} explanation={draft.explanation} busy={busy}
        author={data.template.owner_id === profile?.user_id ? 'you' : data.template.owner_name || data.template.owner_email}
        // Once proposals exist they are what the reviewer came for; before that, or when an older
        // submission was picked to read, the data is what the page is about.
        initiallyOpen={!!historical || !REVIEWING.includes(data.template.status)}
        onDownloadFile={fileId => void intakeDownload(templateId!, fileId, historical || undefined).catch(e => setError(e.message))}
        onDownloadNotes={() => downloadText(draftRef.current, 'explanation.txt')}
        editor={data.can_edit_source ? {
          onChange: explanation => update({ ...draftRef.current, explanation }),
          onAttach: files => void upload(files),
          onRemove: fileId => mutate(async () => {
            receive(await api.post<IntakeDetail>(`${base}/files/${fileId}/remove`, { revision: current.current!.template.revision }))
          }),
          onSave: save, status: conflict ? 'Saving paused: reload the latest version before continuing.' : status,
          progress, changed: !!data.source_changed,
        } : undefined} />
    </>}
    {!historical && (isSubmitted(data.template.status) || data.template.status === 'approved') && <ResearchReview developer={developer} id={templateId!} revision={data.template.revision}
      refresh={refreshSource} sourceChanged={!!data.source_changed}
      externalBusy={busy || conflict} onBusyChange={active => { busyRef.current = active; setBusy(active) }}
      beforeDecision={async action => {
        await save()
        const latest = current.current!
        if (['', '/approve'].includes(action) && (!draftRef.current.explanation.trim() || draftRef.current.partial || !latest.files.length || latest.files.some(file => file.status !== 'uploaded')))
          throw new Error('Finish the template explanation and at least one data upload before deciding.')
        return latest.template.revision
      }} />}
    {error && (!conflict || data.can_edit_source) && <div className="note err" role="alert"><p>{error}</p>
      {conflict && <div className="row">
        <button className="ghost" onClick={() => downloadText(draftRef.current, 'recovered-explanation.txt')}>Download recovered explanation</button>
        <button disabled={busy} onClick={() => void api.get<IntakeDetail>(base).then(async value => {
          const text = textOf(value); draftRef.current = text; setDraft(text); receive(value); saved.current = JSON.stringify(text)
          blocked.current = false; setConflict(false); setError(''); setStatus('Saved'); await recovery.remove(cacheKey).catch(() => {})
        }).catch(e => setError(e.message))}>Use latest server version</button>
      </div>}
    </div>}
    {developer && isSubmitted(data.template.status) && !historical && <section className="box">
      <label className="field"><span>Changes needed</span><textarea value={reason} maxLength={2000} onChange={e => setReason(e.target.value)} /></label>
      <button disabled={busy || !reason.trim()} onClick={() => void mutate(async () => {
        receive(await api.post<IntakeDetail>(`${base}/return`, { revision: current.current!.template.revision, reason }))
      })}>Return for edits</button>
    </section>}
  </div>
}
